import React from "react";
import { useTranslation } from "react-i18next";
import { User } from "../../../../types";
import Button from "@/components/ui/button";
import { constants } from "../../../../constants";
import { Lock } from "lucide-react";

type Theme = {
  id: number;
  name: string;
  className: string;
  premium?: boolean;
};

interface ThemeCustomizationProps {
  colorScheme: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  user?: User;
}

const themes: Theme[] = [
  { id: 0, name: "Classic", className: "classic" },
  { id: 1, name: "noir", className: "night" },
  { id: 2, name: "sparkle", className: "sparkle", premium: true },
  { id: 3, name: "neon", className: "neon", premium: true },
  { id: 4, name: "blue", className: "blue", premium: true },
  { id: 5, name: "90s", className: "nineties", premium: true },
  { id: 6, name: "petal", className: "petal", premium: true },
  { id: 7, name: "blood", className: "blood", premium: true },
  { id: 8, name: "worm", className: "worm", premium: true },
  { id: 9, name: "goth", className: "goth", premium: true },
  { id: 10, name: "hacker", className: "hacker", premium: true },
  { id: 11, name: "winter", className: "winter", premium: true },
];

const ThemeCustomization: React.FC<ThemeCustomizationProps> = ({
  colorScheme,
  onChange,
  user,
}) => {
  const { t } = useTranslation("main", { keyPrefix: "profile" });

  // Track preview index separately from actual selected theme
  const [previewIndex, setPreviewIndex] = React.useState<number>(() => {
    return themes.findIndex((theme) => theme.id === colorScheme);
  });

  // Update preview when colorScheme changes externally
  React.useEffect(() => {
    const index = themes.findIndex((theme) => theme.id === colorScheme);
    if (index !== -1) {
      setPreviewIndex(index);
    }
  }, [colorScheme]);

  const currentTheme = themes[previewIndex] || themes[0];

  const handlePrevious = () => {
    const prevIndex = previewIndex > 0 ? previewIndex - 1 : themes.length - 1;
    setPreviewIndex(prevIndex);

    const prevTheme = themes[prevIndex];

    // Only update Redux if theme is not premium or user has premium
    if (!prevTheme.premium || user?.premium) {
      onChange({
        target: { value: prevTheme.id.toString() },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleNext = () => {
    const nextIndex = previewIndex < themes.length - 1 ? previewIndex + 1 : 0;
    setPreviewIndex(nextIndex);

    const nextTheme = themes[nextIndex];

    // Only update Redux if theme is not premium or user has premium
    if (!nextTheme.premium || user?.premium) {
      onChange({
        target: { value: nextTheme.id.toString() },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  // Handle keyboard arrow keys
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      handlePrevious();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      handleNext();
    }
  };

  return (
    <div className="w-full max-w-md flex flex-col space-y-2">
      {/* Carousel with left/right arrows */}
      <div
        className="flex justify-around items-center gap-2 w-full"
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <Button
          size={"7xl"}
          type="button"
          onClick={handlePrevious}
          tabIndex={-1}
        >
          {constants.leftButton}
        </Button>

        {/* Theme Preview Box */}
        <div className="flex flex-col items-center gap-2">
          <div
            className={`relative flex items-center justify-center w-40 h-40 ${currentTheme.premium && !user?.premium ? "opacity-60" : ""}`}
          >
            {/* Background preview with theme applied */}
            <div
              className={`absolute inset-0 franticfanficbg ${currentTheme.className} rounded border-2 border-black`}
            />

            {/* Theme name label */}
            <div className="relative z-10 bg-black bg-opacity-70 px-2 py-1 rounded">
              <div
                className={`text-sm leading-tight flex items-center gap-1 ${currentTheme.premium && !user?.premium ? "text-gray-400" : "text-white"}`}
              >
                {currentTheme.name}
                {currentTheme.premium && !user?.premium && <Lock size={14} />}
              </div>
            </div>
          </div>
        </div>

        <Button
          size={"7xl"}
          type="button"
          onClick={handleNext}
          tabIndex={-1}
        >
          {constants.rightButton}
        </Button>
      </div>

      {!user?.premium && (
        <div className="text-center text-sm">{t("body.extraThemes")}</div>
      )}
    </div>
  );
};

export default ThemeCustomization;
